import { FC, Suspense, useCallback, useContext } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { InsertInvitation } from '@mui/icons-material';
import { Button, Stack, Typography, useMediaQuery } from '@mui/material';

import { useRouter } from '@cvt/routing';
import ErrorBoundary from '@cvt/components/ErrorBoundary';
import { DialogContext } from '@cvt/contexts/DialogContext/DialogContext';
import { useDictionary } from '@cvt/hooks/useDictionary';

import { BoxWell } from '@shared/components/BoxWell';

import { MeetingsFilters, Props as MeetingsFiltersProps } from '../partials/MeetingsFilters';
import { MeetingItemSkeleton } from '../components/MeetingItemSkeleton';
import { MeetingList } from '../partials/MeetingList';
import { MeetingListItem } from '../components/MeetingListItem';
import { useMeetingCrud } from '../hooks/useMeetingCrud';
import { MeetingListTabs } from '../partials/MeetingListTabs';


export interface Props extends MeetingsFiltersProps {
}

export const ViewMeetings: FC<Props> = (props) => {
  const { filters, setters, disabledFilters } = props;
  const router = useRouter();
  const location = useLocation();
  const dictionary = useDictionary();
  const { asyncConfirmation } = useContext(DialogContext);

  const { deleteMeeting } = useMeetingCrud();

  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const onDelete = useCallback(async (id: number) => {
    const confirmed = await asyncConfirmation({ title: dictionary.meetings.edit.deleteConfirmation });
    if (!confirmed) {
      return false;
    }
    try {
      await deleteMeeting.mutateAsync(id);
    } catch (error) {
      console.error(error);
    }
  }, [asyncConfirmation, dictionary, deleteMeeting]);

  const tabRoutes = [
    'upcoming',
    'past',
    'all',
  ];

  return (
    <Stack gap={2} width="100%">
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems="center" justifyContent="space-between">
        <Stack spacing={2}>
          <Typography variant="h4" component="h1" fontWeight={600} fontSize={{ xs: 24, sm: 30 }} textAlign={{ xs: 'center', sm: 'left' }}>
            Meeting Planner
          </Typography>
          <Typography variant="body1" fontSize={16} color="text.secondary" fontWeight={500} textAlign={{ xs: 'center', sm: 'left' }}>
            Prepare your narratives for high-impact presentations
          </Typography>
        </Stack>
        <Button
          variant="contained"
          color="primary"
          onClick={() => router.meetings.create.go()}
          startIcon={<InsertInvitation/>}
          size="large"
          fullWidth={isMobile}
        >
          Schedule Meeting
        </Button>
      </Stack>

      <BoxWell>
        <MeetingsFilters
          filters={filters}
          setters={setters}
          disabledFilters={disabledFilters}
        />
      </BoxWell>

      <MeetingListTabs/>

      <ErrorBoundary>
        <Suspense
          key={location.key}
          fallback={(
            <MeetingItemSkeleton/>
          )}
        >
          <Routes>
            {tabRoutes.map(route => (
              <Route
                key={route}
                path={route}
                element={(
                  <MeetingList
                    filters={filters}
                    renderItem={meeting => <MeetingListItem key={meeting.id} {...meeting} onDelete={onDelete} showFeedback={['all', 'past'].includes(route)}/>}
                  />
                )}
              />
            ))}
            <Route path="*" element={<Navigate to={router.meetings.list.upcoming.path} replace/>}/>
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </Stack>
  );
};
